import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { getCustomerDebtReport, getCustomerInteractionSummary, getCustomerAssignments, getSettings, getDocumentDetails, getCustomerOpenInvoices, listAllDocuments } from '../api/client';
import type { CustomerAssignedMember, CustomerOpenInvoice, CustomerReconciliation } from '../api/client';
import { rivhitDisplayName, useRivhitData, type RivhitCustomer } from '../context/RivhitDataContext';
import { useAuth } from '../context/AuthContext';
import CustomerInteractionLog from './CustomerInteractionLog';
import CustomerContactsEditor from './CustomerContactsEditor';
import CustomerReconciliationPanel from './CustomerReconciliationPanel';
import CustomerActivities from './CustomerActivities';
import DebtReportSendModal from './DebtReportSendModal';
import ManageAssignmentsModal from './ManageAssignmentsModal';
import { normalizeIsraeliPhoneNumber } from '../lib/phone';
import { printOpenInvoicesReport } from '../lib/printOpenInvoicesReport';

// ── Types ────────────────────────────────────────────────────────────

interface CustomerDebtItemSummary {
  product_id: string;
  product_name: string;
  total_quantity: number;
  order_count: number;
  typical_unit: string;
  last_ordered_at?: string | null;
  last_price?: number | null;
  avg_monthly_3m?: number | null;
  avg_monthly_6m?: number | null;
}

interface CustomerDebtMonth {
  month: string;
  invoices_amount: number;
  paid_amount: number;
  unpaid_amount: number;
  due_date: string;
  status: 'paid' | 'overdue' | 'due_soon' | 'current';
}

interface QuarterMonthAmount {
  month: string;
  quantity: number;
}

interface QuarterItemSummary {
  product_id: string;
  product_name: string;
  typical_unit: string;
  total_quantity: number;
  last_price?: number | null;
  monthly_amounts: QuarterMonthAmount[];
}

interface CustomerRecentDocument {
  document_category?: 'invoice' | 'receipt' | 'invoice_receipt' | 'delivery_note' | string;
  document_type: number;
  document_type_name: string;
  document_number: number;
  document_date: string;
  amount: number;
  balance?: number | null;
  open_amount?: number | null;
  balance_status?: 'provider' | 'local_overlay' | 'closure_metadata' | 'unknown';
  document_link?: string | null;
  closed_invoices?: ReceiptClosedInvoice[];
}

interface UnifiedDocumentItem {
  reconciliation?: CustomerReconciliation | null;
  document_category?: 'invoice' | 'receipt' | 'invoice_receipt' | 'delivery_note' | string;
  document_type: number;
  document_type_name: string;
  document_number: number;
  document_date: string;
  amount: number;
  balance?: number | null;
  open_amount?: number | null;
  balance_status?: 'provider' | 'local_overlay' | 'closure_metadata' | 'unknown';
  document_link?: string | null;
  closing_documents?: Array<{
    document_category?: string;
    document_type: number;
    document_number: number;
    amount: number;
    close_status?: string;
  }>;
}

interface ReceiptClosedInvoice {
  document_type: number;
  document_type_name?: string;
  document_number: number;
  document_date?: string;
  amount_closed: number;
}

interface DocumentLineItem {
  item_id?: number;
  description?: string;
  catalog_number?: string;
  quantity: number;
  price_nis?: number;
  total_nis?: number;
}

interface DocumentVatSummary {
  subtotal?: number | null;
  vat_rate?: number | null;
  vat_amount?: number | null;
  total?: number | null;
}

export interface CustomerDebtDetail {
  reconciliation?: CustomerReconciliation | null;
  customer_id: number;
  customer_name: string;
  phone?: string | null;
  email?: string | null;
  city?: string | null;
  period: string;
  from_date: string;
  to_date: string;
  payment_terms_days: number;
  current_balance: number;
  invoices_amount: number;
  paid_amount: number;
  unpaid_amount: number;
  aged_debt_amount: number;
  overdue_amount: number;
  uncertain_open_invoices_count?: number;
  uncertain_document_numbers?: number[];
  items: CustomerDebtItemSummary[];
  monthly_debt: CustomerDebtMonth[];
  recent_order_from_date?: string | null;
  recent_order_to_date?: string | null;
  recent_order_months?: string[];
  recent_order_items?: QuarterItemSummary[];
  recent_documents?: CustomerRecentDocument[];
}

// ── Props ────────────────────────────────────────────────────────────

interface CustomerDetailsModalProps {
  operationalOnly?: boolean;
  operationalCustomerId?: string;
  provider?: string;
  /** Full RivhitCustomer when available (Customers / Documents pages). */
  customer?: RivhitCustomer;
  /** Minimal fallback: at least a customer_id is required when customer is not available. */
  customerId?: number;
  /** Display name fallback when no RivhitCustomer is provided. */
  displayName?: string;
  onClose: () => void;
  onCollectionContact?: () => void;
  onActivitySaved?: () => void;
  /** Called when allow_message is toggled. Parent should update its state. */
  onAllowMessageChange?: (customerId: number, allow_message: boolean) => Promise<void>;
  /** When true, scroll to and highlight the Messaging section on open. */
  focusMessaging?: boolean;
  extraContent?: ReactNode;
  /** Called when the user clicks the Edit button (only shown when provided). */
  onEdit?: () => void;
}

// ── Helpers ──────────────────────────────────────────────────────────

function formatNIS(amount: number): string {
  return `₪${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function normalizeDateValue(value?: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return trimmed.slice(0, 10);
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 8) {
    if (digits.startsWith('19') || digits.startsWith('20')) {
      return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
    }
    const day = digits.slice(0, 2);
    const month = digits.slice(2, 4);
    const year = digits.slice(4, 8);
    return `${year}-${month}-${day}`;
  }
  return null;
}

function formatDate(value?: string | null, locale?: string): string {
  if (!value) return '—';
  const normalized = normalizeDateValue(value);
  if (!normalized) return value;
  const d = new Date(`${normalized}T00:00:00`);
  return d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function toIsoDate(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getTodayIsoDate(): string {
  return toIsoDate(new Date());
}

function getMonthsAgoIsoDate(months: number): string {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth() - months, now.getDate()));
}

function formatDateForApi(value?: string): string | undefined {
  if (!value) return undefined;
  const normalized = normalizeDateValue(value);
  if (!normalized) return undefined;
  const [year, month, day] = normalized.split('-');
  return `${day}${month}${year}`;
}

function formatMonthLabel(month: string, locale?: string): string {
  const d = new Date(`${month}-01T00:00:00`);
  return d.toLocaleDateString(locale || undefined, { year: 'numeric', month: 'short' });
}

function LoadingValue({ className = 'h-8 w-24' }: { className?: string }) {
  return <span className={`inline-block animate-pulse rounded-md bg-gray-200 ${className}`} />;
}

const STATUS_CLASSES: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  overdue: 'bg-red-100 text-red-700',
  due_soon: 'bg-amber-100 text-amber-700',
  current: 'bg-slate-100 text-slate-700',
};

const ITEM_EXPANDABLE_CATEGORIES = new Set(['invoice', 'invoice_receipt', 'delivery_note']);
type DocumentCategoryFilter = 'all' | 'invoice' | 'receipt' | 'invoice_receipt' | 'delivery_note';

const DOCUMENT_FILTER_CHIPS: Array<{ id: DocumentCategoryFilter; labelKey: string; fallback: string }> = [
  { id: 'all', labelKey: 'documents.filterAll', fallback: 'All' },
  { id: 'invoice', labelKey: 'documents.filterInvoices', fallback: 'Invoices' },
  { id: 'receipt', labelKey: 'documents.filterReceipts', fallback: 'Receipts' },
  { id: 'invoice_receipt', labelKey: 'documents.filterInvoiceReceipts', fallback: 'Invoice Receipts' },
  { id: 'delivery_note', labelKey: 'documents.filterDeliveryNotes', fallback: 'Delivery Notes' },
];

// ── Component ────────────────────────────────────────────────────────

export default function CustomerDetailsModal(props: CustomerDetailsModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!props.operationalOnly) return;
    const previous = document.activeElement;
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') props.onClose();
      if (event.key !== 'Tab') return;
      const elements = Array.from(dialog.current?.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href]',
      ) ?? []).filter(element => element.offsetParent !== null);
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('keydown', close);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [props.operationalOnly, props.onClose]);
  if (!props.operationalOnly) return <FinancialCustomerDetailsModal {...props} />;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div ref={dialog} role="dialog" aria-modal="true" aria-label={props.displayName}
      className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{props.displayName}</h2>
        <button autoFocus className="rounded border px-3 py-2" onClick={props.onClose}>{t('customerWork.close')}</button>
      </div>
      <CustomerActivities key={`${user?.tenant_id}:${props.provider}:${props.operationalCustomerId ?? props.customerId}`}
        customer={{ customer_id: props.operationalCustomerId ?? String(props.customerId), provider: props.provider,
          customer_name: props.displayName ?? String(props.customerId) }} onSaved={props.onActivitySaved} />
    </div>
  </div>;
}

function FinancialCustomerDetailsModal({
  customer,
  customerId: externalCustomerId,
  displayName: externalDisplayName,
  onClose,
  onCollectionContact,
  onActivitySaved,
  onAllowMessageChange,
  focusMessaging = false,
  extraContent,
  onEdit,
}: CustomerDetailsModalProps) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { updateCustomer } = useRivhitData();
  const locale = i18n.language;

  const resolvedId = customer?.customer_id ?? externalCustomerId ?? 0;
  const resolvedName = customer ? rivhitDisplayName(customer) : externalDisplayName || `#${resolvedId}`;

  const [detail, setDetail] = useState<CustomerDebtDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openInvoices, setOpenInvoices] = useState<CustomerOpenInvoice[]>([]);
  const [openInvoicesLoading, setOpenInvoicesLoading] = useState(false);
  const [openInvoicesError, setOpenInvoicesError] = useState<string | null>(null);
  const [expandedDebtMonths, setExpandedDebtMonths] = useState<Set<string>>(new Set());
  const [debtAgeThresholdDays, setDebtAgeThresholdDays] = useState(60);
  const [expandedReceipts, setExpandedReceipts] = useState<Set<string>>(new Set());
  const [expandedItemDocs, setExpandedItemDocs] = useState<Set<string>>(new Set());
  const [docLineItems, setDocLineItems] = useState<Record<string, DocumentLineItem[]>>({});
  const [docVatSummary, setDocVatSummary] = useState<Record<string, DocumentVatSummary>>({});
  const [docLineItemsLoading, setDocLineItemsLoading] = useState<Set<string>>(new Set());
  const [docLineItemErrors, setDocLineItemErrors] = useState<Record<string, string>>({});
  const [documentCategoryFilter, setDocumentCategoryFilter] = useState<DocumentCategoryFilter>('all');
  const [documentDateFrom, setDocumentDateFrom] = useState(() => getMonthsAgoIsoDate(3));
  const [documentDateTo, setDocumentDateTo] = useState(() => getTodayIsoDate());
  const [openInvoicesOnly, setOpenInvoicesOnly] = useState(false);
  const [filteredDocuments, setFilteredDocuments] = useState<CustomerRecentDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [printingDebtReport, setPrintingDebtReport] = useState(false);
  const [printDebtReportError, setPrintDebtReportError] = useState<string | null>(null);
  const [showDebtReportSend, setShowDebtReportSend] = useState(false);
  const [phoneValue, setPhoneValue] = useState(customer?.phone || '');
  const [phoneDraft, setPhoneDraft] = useState(customer?.phone || '');
  const [editingPhone, setEditingPhone] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [phoneEditError, setPhoneEditError] = useState<string | null>(null);
  const [phoneEditSaved, setPhoneEditSaved] = useState(false);

  // Messaging permission state
  const [allowMessage, setAllowMessage] = useState<boolean>(customer?.allow_message ?? false);
  const [savingAllowMessage, setSavingAllowMessage] = useState(false);
  const [allowMessageError, setAllowMessageError] = useState<string | null>(null);
  const [showWhatsAppConfirm, setShowWhatsAppConfirm] = useState(false);
  const [showInteractionLog, setShowInteractionLog] = useState(false);
  const [interactionSummary, setInteractionSummary] = useState<{ total_interactions: number; last_interaction_at: string | null } | null>(null);
  const [interactionRevision, setInteractionRevision] = useState(0);
  const [interactionSummaryError, setInteractionSummaryError] = useState(false);

  // Assignments state
  const [assignments, setAssignments] = useState<CustomerAssignedMember[]>([]);
  const [showManageAssignments, setShowManageAssignments] = useState(false);
  const [assignmentsSaved, setAssignmentsSaved] = useState(false);
  const canManageAssignments = user?.role === 'owner' || user?.role === 'admin';
  const messagingSectionRef = useCallback((node: HTMLDivElement | null) => {
    if (node && focusMessaging) {
      setTimeout(() => node.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    }
  }, [focusMessaging]);

  // Keep allowMessage in sync if parent updates the customer prop
  useEffect(() => {
    setAllowMessage(customer?.allow_message ?? false);
  }, [customer?.allow_message]);

  const currentContacts = useRef({ customer, revision: 0 });
  useEffect(() => {
    currentContacts.current = { customer, revision: currentContacts.current.revision + 1 };
    setDetail((current) => current ? {
      ...current,
      phone: customer?.phone !== undefined ? customer.phone : current.phone,
      email: customer?.email !== undefined ? customer.email : current.email,
      city: customer?.city !== undefined ? customer.city : current.city,
    } : current);
  }, [customer?.phone, customer?.email, customer?.city]);

  useEffect(() => {
    if (editingPhone) return;
    const nextPhone = detail?.phone || customer?.phone || '';
    setPhoneValue(nextPhone);
    setPhoneDraft(nextPhone);
  }, [customer?.phone, detail?.phone, editingPhone]);

  // Load settings once
  useEffect(() => {
    getSettings()
      .then((res) => {
        const data = res.data?.settings ?? res.data ?? {};
        const v = Number(data.collections?.debt_age_threshold_days ?? data.debt_age_threshold_days ?? 60);
        setDebtAgeThresholdDays(Number.isFinite(v) && v > 0 ? v : 60);
      })
      .catch(() => {
        setDebtAgeThresholdDays(60);
      });
  }, []);

  // Fetch customer debt report
  useEffect(() => {
    if (!resolvedId) return;
    let active = true;
    setLoading(true);
    setError(null);
    const contactRevision = currentContacts.current.revision;

    getCustomerDebtReport(resolvedId)
      .then((res) => {
        if (!active) return;
        const latest = currentContacts.current;
        const contacts = latest.revision !== contactRevision ? latest.customer : undefined;
        setDetail({
          ...res.data,
          ...(contacts?.phone !== undefined ? { phone: contacts.phone } : {}),
          ...(contacts?.email !== undefined ? { email: contacts.email } : {}),
          ...(contacts?.city !== undefined ? { city: contacts.city } : {}),
        });
      })
      .catch(() => { if (active) { setDetail(null); setError(t('customers.detailsError')); } })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [resolvedId, t]);

  // Load invoice-level debt details once so every monthly bucket can expand
  // without being limited by the recent-documents date filter below.
  useEffect(() => {
    if (!resolvedId) return;
    let active = true;
    setOpenInvoicesLoading(true);
    setOpenInvoicesError(null);
    setExpandedDebtMonths(new Set());

    getCustomerOpenInvoices(resolvedId)
      .then((res) => {
        if (active) setOpenInvoices(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!active) return;
        setOpenInvoices([]);
        setOpenInvoicesError(t('reports.openInvoicesError', 'Failed to load open invoices'));
      })
      .finally(() => {
        if (active) setOpenInvoicesLoading(false);
      });

    return () => { active = false; };
  }, [resolvedId, t]);

  const openInvoicesByMonth = useMemo(() => {
    const grouped: Record<string, CustomerOpenInvoice[]> = {};
    for (const invoice of openInvoices) {
      const normalized = normalizeDateValue(invoice.document_date);
      if (!normalized) continue;
      const month = normalized.slice(0, 7);
      (grouped[month] ||= []).push(invoice);
    }
    for (const invoices of Object.values(grouped)) {
      invoices.sort((a, b) => {
        const dateCompare = (normalizeDateValue(a.document_date) || '').localeCompare(normalizeDateValue(b.document_date) || '');
        return dateCompare || a.document_number - b.document_number;
      });
    }
    return grouped;
  }, [openInvoices]);
  const unknownOpenInvoices = useMemo(
    () => openInvoices.filter((invoice) => invoice.balance_status === 'unknown'),
    [openInvoices],
  );

  const toggleDebtMonth = useCallback((month: string) => {
    setExpandedDebtMonths((current) => {
      const next = new Set(current);
      if (next.has(month)) next.delete(month);
      else next.add(month);
      return next;
    });
  }, []);

  // Fetch document table data independently from the debt report so filters do
  // not affect the customer KPIs or order/debt sections.
  useEffect(() => {
    if (!resolvedId) return;
    let active = true;
    setDocumentsLoading(true);
    setDocumentsError(null);

    const params: {
      customer_id: number;
      from_date?: string;
      to_date?: string;
      category_filter?: string;
      open_only?: boolean;
      limit: number;
      offset: number;
    } = {
      customer_id: resolvedId,
      limit: 100,
      offset: 0,
    };
    const fromDate = formatDateForApi(documentDateFrom);
    const toDate = formatDateForApi(documentDateTo);
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (openInvoicesOnly) {
      params.open_only = true;
    } else if (documentCategoryFilter !== 'all') {
      params.category_filter = documentCategoryFilter;
    }

    listAllDocuments(params)
      .then((res) => {
        if (!active) return;
        const items = (res.data?.items || []) as UnifiedDocumentItem[];
        const mapped = items.map((doc) => ({
          document_category: doc.document_category || 'invoice',
          document_type: doc.document_type,
          document_type_name: doc.document_type_name,
          document_number: doc.document_number,
          document_date: doc.document_date,
          amount: doc.amount,
          balance: doc.balance,
          open_amount: doc.open_amount,
          balance_status: doc.balance_status,
          document_link: doc.document_link,
          closed_invoices: (doc.closing_documents || []).map((link) => ({
            document_type: link.document_type,
            document_number: link.document_number,
            amount_closed: link.amount,
          })),
        }));
        setFilteredDocuments(mapped);
      })
      .catch(() => {
        if (!active) return;
        setFilteredDocuments([]);
        setDocumentsError(t('documents.serverError', 'The accounting service encountered an error. Please try again in a moment.'));
      })
      .finally(() => {
        if (active) setDocumentsLoading(false);
      });

    return () => { active = false; };
  }, [documentCategoryFilter, documentDateFrom, documentDateTo, openInvoicesOnly, resolvedId, t]);

  useEffect(() => {
    setExpandedReceipts(new Set());
    setExpandedItemDocs(new Set());
  }, [documentCategoryFilter, documentDateFrom, documentDateTo, openInvoicesOnly, resolvedId]);

  // Fetch interaction summary
  useEffect(() => {
    if (!resolvedId) return;
    let active = true;
    setInteractionSummary(null);
    setInteractionSummaryError(false);
    getCustomerInteractionSummary(String(resolvedId))
      .then((res) => { if (active) setInteractionSummary(res.data); })
      .catch(() => { if (active) setInteractionSummaryError(true); });
    return () => { active = false; };
  }, [interactionRevision, resolvedId]);

  // Fetch assignments
  useEffect(() => {
    if (!resolvedId) return;
    let active = true;
    getCustomerAssignments(resolvedId)
      .then((res) => { if (active) setAssignments(res.assigned_members); })
      .catch(() => { /* ignore — assignments are optional */ });
    return () => { active = false; };
  }, [resolvedId]);

  // Derived data
  const phone = phoneValue || null;
  const email = detail?.email || customer?.email;
  const city = detail?.city || customer?.city;
  const paymentDays = detail?.payment_terms_days || debtAgeThresholdDays;

  const savePhone = useCallback(async () => {
    if (!resolvedId || savingPhone) return;
    setSavingPhone(true);
    setPhoneEditError(null);
    setPhoneEditSaved(false);
    try {
      await updateCustomer(resolvedId, { phone: phoneDraft.trim() });
      const normalizedPhone = normalizeIsraeliPhoneNumber(phoneDraft);
      setPhoneValue(normalizedPhone);
      setPhoneDraft(normalizedPhone);
      setDetail((current) => current ? { ...current, phone: normalizedPhone || null } : current);
      setEditingPhone(false);
      setPhoneEditSaved(true);
    } catch (err: any) {
      setPhoneEditError(
        err?.response?.data?.detail
        || t('customers.phoneEditError', 'Failed to update phone number'),
      );
    } finally {
      setSavingPhone(false);
    }
  }, [phoneDraft, resolvedId, savingPhone, t, updateCustomer]);

  const toggleReceipt = useCallback((key: string) => {
    setExpandedReceipts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleItemDoc = useCallback(async (doc: CustomerRecentDocument, key: string) => {
    if (expandedItemDocs.has(key)) {
      setExpandedItemDocs((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      return;
    }

    setExpandedItemDocs((prev) => new Set(prev).add(key));
    if (docLineItems[key]) return;

    setDocLineItemsLoading((prev) => new Set(prev).add(key));
    setDocLineItemErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    try {
      const res = await getDocumentDetails(doc.document_type, doc.document_number);
      setDocLineItems((prev) => ({ ...prev, [key]: res.data.items || [] }));
      setDocVatSummary((prev) => ({
        ...prev,
        [key]: {
          subtotal: res.data.subtotal,
          vat_rate: res.data.vat_rate,
          vat_amount: res.data.vat_amount,
          total: res.data.total,
        },
      }));
    } catch {
      setDocLineItemErrors((prev) => ({
        ...prev,
        [key]: t('reports.documentItemsError', 'Failed to load document items'),
      }));
    } finally {
      setDocLineItemsLoading((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [docLineItems, expandedItemDocs, t]);

  const documentCategoryLabel = useCallback((category?: string) => {
    switch (category) {
      case 'receipt':
        return t('documents.categoryReceipt', 'Receipt');
      case 'invoice_receipt':
        return t('documents.categoryInvoiceReceipt', 'Invoice Receipt');
      case 'delivery_note':
        return t('documents.categoryDeliveryNote', 'Delivery Note');
      case 'invoice':
      default:
        return t('documents.categoryInvoice', 'Invoice');
    }
  }, [t]);

  const documentCategoryClass = useCallback((category?: string) => {
    switch (category) {
      case 'receipt':
        return 'bg-green-100 text-green-700';
      case 'invoice_receipt':
        return 'bg-purple-100 text-purple-700';
      case 'delivery_note':
        return 'bg-cyan-100 text-cyan-700';
      case 'invoice':
      default:
        return 'bg-blue-100 text-blue-700';
    }
  }, []);

  const handleAllowMessageToggle = useCallback(async (newValue: boolean) => {
    if (!onAllowMessageChange) return;
    setSavingAllowMessage(true);
    setAllowMessageError(null);
    try {
      await onAllowMessageChange(resolvedId, newValue);
      setAllowMessage(newValue);
    } catch (err: any) {
      setAllowMessageError(err?.response?.data?.detail || t('customers.allowMessageError', 'Failed to save messaging setting'));
    } finally {
      setSavingAllowMessage(false);
    }
  }, [onAllowMessageChange, resolvedId, t]);

  const handlePrintDebtReport = useCallback(async () => {
    if (!resolvedId) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setPrintDebtReportError(t('invoiceReport.popupBlocked', 'Could not open the print window. Please allow pop-ups and try again.'));
      return;
    }
    setPrintingDebtReport(true);
    setPrintDebtReportError(null);
    try {
      const allOpenInvoices: CustomerRecentDocument[] = [];
      const limit = 10000;
      let offset = 0;
      let hasMore = true;
      let balanceIncomplete = false;
      let balanceDiscrepancy: number | null = null;
      const toDate = formatDateForApi(getTodayIsoDate());
      while (hasMore) {
        const res = await listAllDocuments({
          customer_id: resolvedId,
          open_only: true,
          from_date: '01011970',
          to_date: toDate,
          limit,
          offset,
        });
        const items = (res.data?.items || []) as UnifiedDocumentItem[];
        balanceIncomplete ||= (res.data.summary?.uncertain_open_invoices_count || 0) > 0;
        for (const item of items) {
          if (item.reconciliation) {
            balanceIncomplete ||= !item.reconciliation.complete;
            balanceDiscrepancy = item.reconciliation.discrepancy;
          }
        }
        allOpenInvoices.push(...items.map((doc) => ({
          document_category: doc.document_category || 'invoice',
          document_type: doc.document_type,
          document_type_name: doc.document_type_name,
          document_number: doc.document_number,
          document_date: doc.document_date,
          amount: doc.amount,
          balance: doc.balance,
          open_amount: doc.open_amount,
          balance_status: doc.balance_status,
          document_link: doc.document_link,
        })));
        hasMore = Boolean(res.data?.has_more);
        offset += items.length;
        if (items.length === 0) break;
      }

      const opened = printOpenInvoicesReport({
        customerName: resolvedName,
        customerId: resolvedId,
        phone: customer?.phone ?? detail?.phone,
        documents: allOpenInvoices,
        fromDate: '1970-01-01',
        toDate: getTodayIsoDate(),
        t,
        i18n,
        printWindow,
        balanceIncomplete,
        balanceDiscrepancy,
      });
      if (!opened) {
        setPrintDebtReportError(t('invoiceReport.popupBlocked', 'Could not open the print window. Please allow pop-ups and try again.'));
      }
    } catch (err: any) {
      printWindow.close();
      setPrintDebtReportError(err?.response?.data?.detail || t('invoiceReport.loadError', 'Failed to load open invoices for printing.'));
    } finally {
      setPrintingDebtReport(false);
    }
  }, [customer?.phone, detail?.phone, i18n, resolvedId, resolvedName, t]);

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/45 p-4" onClick={onClose}>
      <div
        className="w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white/95 px-6 py-5 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-lg font-bold text-primary-700">
              {resolvedName.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-sm font-medium text-primary-600">{t('reports.customerDebtTitle')}</p>
              <h2 className="text-xl font-bold text-gray-900">{resolvedName}</h2>
              <p className="text-sm text-gray-500">#{resolvedId}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrintDebtReport}
              disabled={loading || printingDebtReport || !resolvedId}
              className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="print-debt-report-button"
            >
              🖨️ {printingDebtReport ? t('invoiceReport.loadingButton', 'Preparing...') : t('invoiceReport.printButton', 'Print Debt Report')}
            </button>
            <button
              type="button"
              onClick={() => setShowDebtReportSend(true)}
              disabled={!resolvedId}
              className="rounded-lg border border-green-200 px-3 py-2 text-sm text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="send-debt-report-button"
            >
              💬 {t('invoiceReport.sendButton', 'Send Debt Report')}
            </button>
            {onEdit && customer && (
              <button
                type="button"
                onClick={onEdit}
                className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                data-testid="edit-customer-button"
              >
                {t('customers.editButton', 'Edit')}
              </button>
            )}
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-500 hover:bg-gray-50">
              {t('common.close', 'Close')}
            </button>
          </div>
        </div>

        <div className="space-y-6 px-6 py-6">
          {/* ── Contact info ── */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 text-sm">
            <div data-testid="customer-phone-field">
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-gray-500">{t('customers.phone')}</span>
                {!editingPhone && (
                  <button
                    type="button"
                    onClick={() => {
                      setPhoneDraft(phone || '');
                      setPhoneEditError(null);
                      setPhoneEditSaved(false);
                      setEditingPhone(true);
                    }}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700"
                    data-testid="edit-customer-phone-button"
                  >
                    {t('customers.editPhone', 'Edit phone')}
                  </button>
                )}
              </div>
              {editingPhone ? (
                <div className="mt-1.5 space-y-2">
                  <input
                    type="tel"
                    value={phoneDraft}
                    onChange={(event) => setPhoneDraft(event.target.value)}
                    placeholder={t('customers.phonePlaceholder')}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    autoFocus
                    data-testid="customer-phone-input"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={savePhone}
                      disabled={savingPhone}
                      className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                      data-testid="save-customer-phone-button"
                    >
                      {savingPhone ? t('customers.savingPhone', 'Saving…') : t('common.save', 'Save')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setPhoneDraft(phone || '');
                        setPhoneEditError(null);
                        setEditingPhone(false);
                      }}
                      disabled={savingPhone}
                      className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="mt-0.5 text-gray-900">{phone || '—'}</p>
              )}
              {phoneEditError && <p className="mt-1 text-xs text-red-600" role="alert">{phoneEditError}</p>}
              {phoneEditSaved && <p className="mt-1 text-xs text-green-700" role="status">{t('customers.phoneSaved', 'Phone number updated')}</p>}
            </div>
            {email && (
              <div>
                <span className="font-medium text-gray-500">{t('customers.email')}</span>
                <p className="mt-0.5 text-gray-900">{email}</p>
              </div>
            )}
            {customer?.street && (
              <div>
                <span className="font-medium text-gray-500">{t('customers.street', 'Street')}</span>
                <p className="mt-0.5 text-gray-900">{customer.street}</p>
              </div>
            )}
            {city && (
              <div>
                <span className="font-medium text-gray-500">{t('customers.city')}</span>
                <p className="mt-0.5 text-gray-900">{city}</p>
              </div>
            )}
            {customer?.id_number != null && customer.id_number !== 0 && (
              <div>
                <span className="font-medium text-gray-500">{t('customers.idNumber', 'ID Number')}</span>
                <p className="mt-0.5 text-gray-900">{customer.id_number}</p>
              </div>
            )}
            {customer?.vat_number != null && customer.vat_number !== 0 && (
              <div>
                <span className="font-medium text-gray-500">{t('customers.vatNumber', 'VAT Number')}</span>
                <p className="mt-0.5 text-gray-900">{customer.vat_number}</p>
              </div>
            )}
          </div>

          <CustomerContactsEditor key={`${user?.tenant_id}:${resolvedId}`} customerId={resolvedId} />
          <CustomerReconciliationPanel key={`balance:${user?.tenant_id}:${resolvedId}`} customerId={resolvedId} />
          {extraContent && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              {extraContent}
            </div>
          )}

          {/* ── Team / Assignments ── */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">
                {t('assignments.teamSection', 'Assigned Team')}
              </h3>
              {canManageAssignments && (
                <button
                  type="button"
                  onClick={() => { setShowManageAssignments(true); setAssignmentsSaved(false); }}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  data-testid="manage-assignments-button"
                >
                  <span>👥</span>
                  {t('assignments.manageButton', 'Manage Assignments')}
                </button>
              )}
            </div>
            {assignments.length === 0 ? (
              <p className="text-xs text-gray-400">
                {t('assignments.noAssignments', 'No members assigned — Admins receive all notifications.')}
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {assignments.map((m) => (
                  <div key={m.user_id} className="flex items-center gap-1.5 rounded-full bg-white border border-gray-200 px-2.5 py-1">
                    {m.picture ? (
                      <img src={m.picture} alt={m.name || m.email || ''} className="w-5 h-5 rounded-full" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-bold">
                        {(m.name || m.email || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="text-xs font-medium text-gray-800">{m.name || m.email}</span>
                    {m.role === 'account_executive' && (
                      <span className="text-[10px] text-primary-600 font-medium">AE</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            {assignmentsSaved && (
              <p className="mt-2 text-xs text-green-600 font-medium">
                ✓ {t('assignments.savedSuccess', 'Assignments updated successfully')}
              </p>
            )}
          </div>

          {/* ── Messaging permission (WhatsApp & SMS) ── */}
          {onAllowMessageChange && (
            <div
              ref={messagingSectionRef}
              className={`rounded-xl border p-4 transition-colors ${focusMessaging ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-200 bg-gray-50'}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-800">{t('customers.allowMessageLabel', 'Messaging')}</div>
                  <div className="mt-0.5 text-xs text-gray-500">
                    {phone
                      ? t('customers.allowMessageDesc', 'Allow sending messages (WhatsApp & SMS) to {{phone}}', { phone })
                      : t('customers.allowMessageNoPhone', 'A valid phone number is required to enable messaging')}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={!phone || savingAllowMessage}
                  onClick={() => {
                    if (!allowMessage) {
                      setShowWhatsAppConfirm(true);
                    } else {
                      handleAllowMessageToggle(false);
                    }
                  }}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 ${
                    allowMessage ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={allowMessage}
                  title={!phone ? t('customers.allowMessageNoPhone', 'A valid phone number is required') : undefined}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      allowMessage ? 'ltr:translate-x-5 rtl:-translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
              {allowMessageError && (
                <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {allowMessageError}
                </div>
              )}
              {savingAllowMessage && (
                <div className="mt-2 text-xs text-gray-500">{t('common.saving', 'Saving...')}</div>
              )}

              {/* WhatsApp Enable Confirmation Dialog */}
              {showWhatsAppConfirm && (
                <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="text-lg">⚠️</span>
                    <div>
                      <p className="text-sm font-medium text-amber-900">
                        {t('customers.whatsappConfirmTitle', 'Enable messaging for this customer?')}
                      </p>
                      <p className="mt-1 text-xs text-amber-800">
                        {t('customers.whatsappConfirmMessage', 'Please verify that the phone number ({{phone}}) is correct and that the customer is aware they will receive messages (WhatsApp & SMS).', { phone })}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setShowWhatsAppConfirm(false)}
                      className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowWhatsAppConfirm(false);
                        handleAllowMessageToggle(true);
                      }}
                      className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                    >
                      {t('customers.whatsappConfirmEnable', 'Yes, enable messaging')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Message Interaction Log ── */}
          <CustomerActivities key={`${user?.tenant_id}:${resolvedId}`}
            customer={{ customer_id: String(resolvedId), customer_name: resolvedName }}
            onSaved={onActivitySaved} />

          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-800">{t('customerBot.interactionLog', 'Message Interactions')}</div>
                <div className="mt-0.5 text-xs text-gray-500">
                  {interactionSummaryError ? <span role="alert" className="text-red-700">{t('collectionWorkflow.historyLoadError')}</span> : interactionSummary
                    ? t('customerBot.interactionSummary', '{{count}} interactions', { count: interactionSummary.total_interactions })
                    : t('common.loading', 'Loading...')}
                  {interactionSummary?.last_interaction_at && (
                    <span className="ml-2">
                      · {t('customerBot.lastInteraction', 'Last')}: {formatDate(interactionSummary.last_interaction_at, locale)}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowInteractionLog(!showInteractionLog)}
                className="text-xs font-medium text-primary-600 hover:text-primary-800 transition-colors"
              >
                {showInteractionLog
                  ? t('common.hide', 'Hide')
                  : t('common.show', 'Show')}
              </button>
            </div>
            {showInteractionLog && (
              <div className="mt-3">
                <CustomerInteractionLog
                  customerId={String(resolvedId)}
                  refreshToken={interactionRevision}
                />
              </div>
            )}
          </div>

          {/* ── KPI cards ── */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="text-sm font-medium text-amber-700">{t('reports.unpaidAmount')}</div>
              <div className="mt-2 text-2xl font-bold text-amber-900">
                {loading ? <LoadingValue className="h-8 w-28 bg-amber-200/70" /> : formatNIS(detail?.unpaid_amount ?? 0)}
              </div>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
              <div className="text-sm font-medium text-rose-700">{t('reports.agedDebtAmount', { days: paymentDays })}</div>
              <div className="mt-2 text-2xl font-bold text-rose-900">
                {loading ? <LoadingValue className="h-8 w-28 bg-rose-200/70" /> : formatNIS(detail?.aged_debt_amount ?? 0)}
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          {printDebtReportError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {printDebtReportError}
            </div>
          )}

          {/* ── Payment terms ── */}
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-sm text-gray-600">
              {t('reports.paymentTerms', { days: paymentDays })}
            </div>
            {loading ? (
              <LoadingValue className="h-5 w-32" />
            ) : phone ? (
              <div className="text-sm text-gray-500">{phone}</div>
            ) : null}
            {loading ? (
              <LoadingValue className="h-5 w-40" />
            ) : email ? (
              <div className="text-sm text-gray-500">{email}</div>
            ) : null}
          </div>

          {/* ── Items consumed (top items all-time) + Monthly debt ── */}
          {loading ? (
            <div className="py-10 text-center text-sm text-gray-500">{t('reports.loadingCustomerDetails')}</div>
          ) : (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_0.9fr]">
              {/* Items consumed */}
              <div className="rounded-xl border border-gray-100 bg-white">
                <div className="border-b border-gray-100 px-5 py-4">
                  <h3 className="text-base font-semibold text-gray-900">{t('reports.itemsConsumed')}</h3>
                </div>
                {detail?.items?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-gray-500">
                          <th className="px-5 py-3 text-start font-medium">{t('reports.item')}</th>
                          <th className="px-5 py-3 text-end font-medium">{t('reports.avgMonthly3m')}</th>
                          <th className="px-5 py-3 text-end font-medium">{t('reports.avgMonthly6m')}</th>
                          <th className="px-5 py-3 text-end font-medium">{t('reports.lastPrice')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.items.map((item) => (
                          <tr key={item.product_id} className="border-t border-gray-50">
                            <td className="px-5 py-3 font-medium text-gray-800">{item.product_name}</td>
                            <td className="px-5 py-3 text-end text-gray-600">
                              {item.avg_monthly_3m != null ? `${item.avg_monthly_3m.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${item.typical_unit}` : '—'}
                            </td>
                            <td className="px-5 py-3 text-end text-gray-600">
                              {item.avg_monthly_6m != null ? `${item.avg_monthly_6m.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${item.typical_unit}` : '—'}
                            </td>
                            <td className="px-5 py-3 text-end text-gray-600">
                              {item.last_price != null ? formatNIS(item.last_price) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-5 py-8 text-center text-sm text-gray-400">{t('reports.noRelevantItems')}</div>
                )}
              </div>

              {/* Monthly debt */}
              <div className="rounded-xl border border-gray-100 bg-white">
                <div className="border-b border-gray-100 px-5 py-4">
                  <h3 className="text-base font-semibold text-gray-900">{t('reports.monthlyDebt')}</h3>
                </div>
                {(unknownOpenInvoices.length > 0 || (detail?.uncertain_open_invoices_count || 0) > 0) && (
                  <div className="mx-5 mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert" data-testid="customer-balance-warning">
                    {t(
                      'documents.uncertainBalanceWarning',
                      '{{count}} invoice balance could not be verified and is excluded from open-debt totals.',
                      { count: Math.max(unknownOpenInvoices.length, detail?.uncertain_open_invoices_count || 0) },
                    )}
                    {(detail?.uncertain_document_numbers?.length || unknownOpenInvoices.length > 0) && (
                      <span className="ms-1 font-medium">
                        #{(detail?.uncertain_document_numbers?.length
                          ? detail.uncertain_document_numbers
                          : unknownOpenInvoices.map((invoice) => invoice.document_number)).join(', #')}
                      </span>
                    )}
                  </div>
                )}
                {detail?.monthly_debt?.length ? (
                  <div className="space-y-3 px-5 py-4">
                    {detail.monthly_debt.map((month) => {
                      const monthInvoices = openInvoicesByMonth[month.month] || [];
                      const isExpanded = expandedDebtMonths.has(month.month);
                      const panelId = `monthly-open-invoices-${month.month}`;
                      return (
                        <div key={month.month} className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="font-semibold text-gray-900">{formatMonthLabel(month.month, locale)}</div>
                            <div className="text-xs text-gray-500">
                              {t('reports.dueDate')}: {formatDate(month.due_date, locale)}
                            </div>
                          </div>
                          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${STATUS_CLASSES[month.status] || ''}`}>
                            {t(`reports.status.${month.status}`)}
                          </span>
                        </div>
                        <div className="mt-4 grid grid-cols-3 gap-3 text-sm">
                          <div>
                            <div className="text-gray-500">{t('reports.invoicesAmount')}</div>
                            <div className="mt-1 font-semibold text-gray-900">{formatNIS(month.invoices_amount)}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">{t('reports.paidAmount')}</div>
                            <div className="mt-1 font-semibold text-green-700">{formatNIS(month.paid_amount)}</div>
                          </div>
                          <div>
                            <div className="text-gray-500">{t('reports.unpaidAmount')}</div>
                            <div className="mt-1 font-semibold text-amber-700">{formatNIS(month.unpaid_amount)}</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleDebtMonth(month.month)}
                          className="mt-4 flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                          aria-expanded={isExpanded}
                          aria-controls={panelId}
                          data-testid={`monthly-open-invoices-toggle-${month.month}`}
                        >
                          <span>
                            {openInvoicesLoading
                              ? t('reports.loadingOpenInvoices', 'Loading open invoices…')
                              : t('reports.openInvoicesCount', { count: monthInvoices.length, defaultValue: '{{count}} open invoices' })}
                          </span>
                          <span className={`inline-block text-xs transition-transform ${isExpanded ? 'rotate-90' : ''}`} aria-hidden="true">▶</span>
                        </button>
                        {isExpanded && (
                          <div id={panelId} className="mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white">
                            {openInvoicesLoading ? (
                              <div className="px-3 py-4 text-sm text-gray-500">{t('reports.loadingOpenInvoices', 'Loading open invoices…')}</div>
                            ) : openInvoicesError ? (
                              <div className="px-3 py-4 text-sm text-red-600">{openInvoicesError}</div>
                            ) : monthInvoices.length === 0 ? (
                              <div className="px-3 py-4 text-sm text-gray-500">{t('reports.noOpenInvoicesForMonth', 'No open invoices found for this month')}</div>
                            ) : (
                              <div className="divide-y divide-gray-100">
                                {monthInvoices.map((invoice) => (
                                  <div key={`${invoice.document_type}-${invoice.document_number}`} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                                    <div className="min-w-0">
                                      <div className="truncate font-medium text-gray-800">
                                        {invoice.document_type_name || t('documents.categoryInvoice', 'Invoice')} #{invoice.document_number}
                                      </div>
                                      <div className="text-xs text-gray-500">{formatDate(invoice.document_date, locale)}</div>
                                    </div>
                                    <div className="shrink-0 text-end">
                                      <div className="font-semibold text-amber-700">
                                        {invoice.balance_status === 'unknown' || invoice.open_balance == null
                                          ? t('documents.openAmountUnavailable', 'Unavailable')
                                          : formatNIS(invoice.open_balance)}
                                      </div>
                                      {invoice.open_balance != null && invoice.amount !== invoice.open_balance && (
                                        <div className="text-xs text-gray-500">
                                          {t('reports.invoiceTotal', 'Total')}: {formatNIS(invoice.amount)}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-5 py-8 text-center text-sm text-gray-400">{t('reports.noData')}</div>
                )}
              </div>
            </div>
          )}

          {/* ── Recent quarter items ── */}
          {!loading && detail?.recent_order_items?.length ? (
            <div className="rounded-xl border border-gray-100 bg-white">
              <div className="border-b border-gray-100 px-5 py-4">
                <h3 className="text-base font-semibold text-gray-900">{t('customers.recentItemsTitle')}</h3>
                {detail.recent_order_from_date && detail.recent_order_to_date && (
                  <p className="mt-1 text-xs text-gray-500">
                    {t('customers.recentPeriod', {
                      from: formatDate(detail.recent_order_from_date, locale),
                      to: formatDate(detail.recent_order_to_date, locale),
                    })}
                  </p>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500">
                      <th className="px-5 py-3 text-start font-medium">{t('reports.item')}</th>
                      {(detail.recent_order_months || []).map((m) => (
                        <th key={m} className="px-4 py-3 text-end font-medium">{formatMonthLabel(m, locale)}</th>
                      ))}
                      <th className="px-5 py-3 text-end font-medium">{t('customers.totalQuantity')}</th>
                      <th className="px-5 py-3 text-end font-medium">{t('customers.itemPrice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.recent_order_items.map((item) => (
                      <tr key={item.product_id || item.product_name} className="border-t border-gray-50">
                        <td className="px-5 py-3 font-medium text-gray-800">{item.product_name}</td>
                        {(detail.recent_order_months || []).map((m) => {
                          const qty = item.monthly_amounts?.find((a) => a.month === m)?.quantity ?? 0;
                          return (
                            <td key={m} className="px-4 py-3 text-end text-gray-600">
                              {qty > 0 ? qty.toLocaleString(undefined, { maximumFractionDigits: 1 }) : '—'}
                            </td>
                          );
                        })}
                        <td className="px-5 py-3 text-end font-semibold text-gray-800">
                          {item.total_quantity.toLocaleString(undefined, { maximumFractionDigits: 1 })} {item.typical_unit}
                        </td>
                        <td className="px-5 py-3 text-end text-gray-600">
                          {item.last_price != null ? formatNIS(item.last_price) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          {/* ── Customer documents ── */}
          {!loading ? (
            <div className="rounded-xl border border-gray-100 bg-white">
              <div className="space-y-3 border-b border-gray-100 px-5 py-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">{t('reports.recentDocuments')}</h3>
                    <p className="mt-0.5 text-xs text-gray-500">
                      {t('reports.documentsFilterDefault', 'Filtered to the last 3 months by default')}
                    </p>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={openInvoicesOnly}
                      onChange={(event) => setOpenInvoicesOnly(event.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                    />
                    {t('documents.openInvoicesOnly', 'Open invoices only')}
                  </label>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {DOCUMENT_FILTER_CHIPS.map((chip) => {
                    const active = openInvoicesOnly ? chip.id === 'invoice' : documentCategoryFilter === chip.id;
                    return (
                      <button
                        key={chip.id}
                        type="button"
                        disabled={openInvoicesOnly}
                        onClick={() => setDocumentCategoryFilter(chip.id)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                          active
                            ? 'border-primary-600 bg-primary-600 text-white'
                            : 'border-gray-300 bg-white text-gray-600 hover:border-primary-400 hover:text-primary-600'
                        } ${openInvoicesOnly ? 'cursor-not-allowed opacity-60' : ''}`}
                      >
                        {t(chip.labelKey, chip.fallback)}
                      </button>
                    );
                  })}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="whitespace-nowrap">{t('documents.dateFrom', 'From date')}</span>
                    <input
                      type="date"
                      value={documentDateFrom}
                      max={documentDateTo || undefined}
                      onChange={(event) => setDocumentDateFrom(event.target.value)}
                      className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="whitespace-nowrap">{t('documents.dateTo', 'To date')}</span>
                    <input
                      type="date"
                      value={documentDateTo}
                      min={documentDateFrom || undefined}
                      onChange={(event) => setDocumentDateTo(event.target.value)}
                      className="rounded-md border border-gray-300 bg-gray-50 px-3 py-1.5 text-sm outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setDocumentDateFrom(getMonthsAgoIsoDate(3));
                      setDocumentDateTo(getTodayIsoDate());
                    }}
                    className="rounded-md border border-gray-300 bg-gray-50 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    {t('documents.rangeLast3Months', 'Last 3 months')}
                  </button>
                </div>
              </div>
              {documentsError ? (
                <div className="px-5 py-4 text-sm text-red-600">{documentsError}</div>
              ) : documentsLoading ? (
                <div className="px-5 py-4 text-sm text-gray-500">{t('documents.loading', 'Loading reports...')}</div>
              ) : filteredDocuments.length === 0 ? (
                <div className="px-5 py-4 text-sm text-gray-500">{t('documents.noDocuments', 'No reports found')}</div>
              ) : (
                <div className="divide-y divide-gray-50">
                {filteredDocuments.map((doc) => {
                  const docKey = `${doc.document_category || 'invoice'}-${doc.document_type}-${doc.document_number}`;
                  const isReceipt = doc.document_category === 'receipt';
                  const canExpandItems = ITEM_EXPANDABLE_CATEGORIES.has(doc.document_category || 'invoice');
                  const closedInvoices = doc.closed_invoices || [];
                  const isReceiptExpanded = expandedReceipts.has(docKey);
                  const isItemsExpanded = expandedItemDocs.has(docKey);
                  const lineItems = docLineItems[docKey] || [];
                  const vatInfo = docVatSummary[docKey];
                  const isLoadingItems = docLineItemsLoading.has(docKey);
                  const itemsError = docLineItemErrors[docKey];

                  return (
                    <div key={docKey}>
                      <div className="flex items-center justify-between gap-3 px-5 py-3">
                        <div className="flex min-w-0 items-center gap-3">
                          {isReceipt && closedInvoices.length > 0 ? (
                            <button
                              type="button"
                              onClick={() => toggleReceipt(docKey)}
                              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                              aria-label={isReceiptExpanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}
                            >
                              <span className={`inline-block text-xs transition-transform ${isReceiptExpanded ? 'rotate-90' : ''}`}>▶</span>
                            </button>
                          ) : canExpandItems ? (
                            <button
                              type="button"
                              onClick={() => toggleItemDoc(doc, docKey)}
                              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                              aria-label={isItemsExpanded ? t('common.collapse', 'Collapse') : t('common.expand', 'Expand')}
                              aria-expanded={isItemsExpanded}
                            >
                              <span className={`inline-block text-xs transition-transform ${isItemsExpanded ? 'rotate-90' : ''}`}>▶</span>
                            </button>
                          ) : (
                            <span className="h-6 w-6 shrink-0" />
                          )}
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${documentCategoryClass(doc.document_category)}`}>
                                {documentCategoryLabel(doc.document_category)}
                              </span>
                              {doc.document_link ? (
                                <a
                                  href={doc.document_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-primary-600 hover:text-primary-800 hover:underline"
                                >
                                  {doc.document_type_name || `#${doc.document_type}`} #{doc.document_number}
                                </a>
                              ) : (
                                <span className="font-medium text-gray-800">
                                  {doc.document_type_name || `#${doc.document_type}`} #{doc.document_number}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              {formatDate(doc.document_date, locale)}
                              {isReceipt && closedInvoices.length > 0
                                ? ` · ${t('reports.closedInvoicesCount', { count: closedInvoices.length, defaultValue: '{{count}} closed invoices' })}`
                                : ''}
                            </p>
                          </div>
                        </div>
                        <div className="text-end whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">{formatNIS(doc.amount)}</div>
                          {doc.balance_status === 'unknown' ? (
                            <div className="text-xs font-medium text-amber-700">
                              {t('documents.openAmountUnavailable', 'Open balance unavailable')}
                            </div>
                          ) : doc.open_amount != null && doc.open_amount > 0 ? (
                            <div className="text-xs font-medium text-amber-700">
                              {t('documents.openAmount', 'Open balance')}: {formatNIS(doc.open_amount)}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      {isReceipt && isReceiptExpanded && (
                        <div className="bg-gray-50/70 px-14 pb-4">
                          {closedInvoices.length > 0 ? (
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-500">
                                  <th className="py-2 text-start font-medium">{t('documents.categoryInvoice', 'Invoice')}</th>
                                  <th className="py-2 text-start font-medium">{t('documents.date')}</th>
                                  <th className="py-2 text-end font-medium">{t('reports.amountClosed', 'Amount closed')}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {closedInvoices.map((invoice) => (
                                  <tr key={`${invoice.document_type}-${invoice.document_number}`}>
                                    <td className="py-2 text-gray-800">
                                      {invoice.document_type_name || `#${invoice.document_type}`} #{invoice.document_number}
                                    </td>
                                    <td className="py-2 text-gray-500">{formatDate(invoice.document_date, locale)}</td>
                                    <td className="py-2 text-end font-semibold text-green-700">{formatNIS(invoice.amount_closed)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          ) : (
                            <div className="py-3 text-xs text-gray-400">{t('reports.noClosedInvoices', 'No closed invoices found for this receipt')}</div>
                          )}
                        </div>
                      )}
                      {canExpandItems && isItemsExpanded && (
                        <div className="bg-gray-50/70 px-14 pb-4">
                          {isLoadingItems ? (
                            <div className="py-3 text-xs text-gray-500">{t('documents.loadingItems', 'Loading items...')}</div>
                          ) : itemsError ? (
                            <div className="py-3 text-xs text-red-600">{itemsError}</div>
                          ) : lineItems.length > 0 ? (
                            <div className="overflow-x-auto">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-500">
                                    <th className="py-2 text-start font-medium">{t('documents.itemDescription', 'Description')}</th>
                                    <th className="py-2 text-start font-medium">{t('documents.itemCatalog', 'Catalog #')}</th>
                                    <th className="py-2 text-end font-medium">{t('documents.itemQuantity', 'Qty')}</th>
                                    <th className="py-2 text-end font-medium">{t('documents.itemPrice', 'Price')}</th>
                                    <th className="py-2 text-end font-medium">{t('documents.itemTotal', 'Total')}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {lineItems.map((item, index) => (
                                    <tr key={`${item.item_id || item.catalog_number || item.description || 'item'}-${index}`}>
                                      <td className="py-2 text-gray-800">{item.description || '—'}</td>
                                      <td className="py-2 text-gray-500">{item.catalog_number || '—'}</td>
                                      <td className="py-2 text-end text-gray-600">
                                        {item.quantity.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                      </td>
                                      <td className="py-2 text-end text-gray-600">
                                        {item.price_nis != null ? formatNIS(item.price_nis) : '—'}
                                      </td>
                                      <td className="py-2 text-end font-semibold text-gray-800">
                                        {item.total_nis != null ? formatNIS(item.total_nis) : '—'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                {vatInfo && (vatInfo.subtotal != null || vatInfo.vat_amount != null || vatInfo.total != null) ? (
                                  <tfoot className="border-t border-gray-200">
                                    {vatInfo.subtotal != null && (
                                      <tr>
                                        <td colSpan={4} className="py-1.5 text-end text-gray-500">{t('documents.subtotal', 'Subtotal')}</td>
                                        <td className="py-1.5 text-end text-gray-700">{formatNIS(vatInfo.subtotal)}</td>
                                      </tr>
                                    )}
                                    {vatInfo.vat_amount != null && (
                                      <tr>
                                        <td colSpan={4} className="py-1.5 text-end text-gray-500">
                                          {t('documents.vat', 'VAT')}{vatInfo.vat_rate != null ? ` (${vatInfo.vat_rate}%)` : ''}
                                        </td>
                                        <td className="py-1.5 text-end text-gray-700">{formatNIS(vatInfo.vat_amount)}</td>
                                      </tr>
                                    )}
                                    {vatInfo.total != null && (
                                      <tr>
                                        <td colSpan={4} className="py-1.5 text-end font-semibold text-gray-700">{t('documents.totalWithVat', 'Total (incl. VAT)')}</td>
                                        <td className="py-1.5 text-end font-semibold text-gray-900">{formatNIS(vatInfo.total)}</td>
                                      </tr>
                                    )}
                                  </tfoot>
                                ) : null}
                              </table>
                            </div>
                          ) : (
                            <div className="py-3 text-xs text-gray-400">{t('documents.noItems', 'No line items')}</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>

    {showManageAssignments && (
      <ManageAssignmentsModal
        customerId={resolvedId}
        customerName={resolvedName}
        currentAssignments={assignments}
        onClose={() => setShowManageAssignments(false)}
        onSaved={(updated) => {
          setAssignments(updated);
          setShowManageAssignments(false);
          setAssignmentsSaved(true);
          setTimeout(() => setAssignmentsSaved(false), 4000);
        }}
      />
    )}

    {showDebtReportSend && (
      <DebtReportSendModal
        customerId={resolvedId}
        customerName={resolvedName}
        phone={phone}
        email={email}
        onClose={() => setShowDebtReportSend(false)}
        onInteractionLogged={() => {
          setInteractionRevision((current) => current + 1);
          setShowInteractionLog(true);
          onCollectionContact?.();
        }}
      />
    )}
    </>
  );
}
